import { useEffect, useRef } from "react";
import { identifyUser, resetAnalyticsUser } from "@/lib/posthog";

type Identity = {
  /** Auth is still resolving: the session is null at this point whether or not someone is signed in. */
  isLoading: boolean;
  userId: string | undefined;
  /** undefined while the player account loads. */
  userType: "player" | "signed_in" | undefined;
  isSuperAdmin: boolean;
};

/** Ties PostHog to the signed-in user (internal id only). Resets only on a real sign-out or user
 *  switch: resetting while auth is still loading would give every visitor a new identity on each
 *  page load and break funnels across the sign-in redirect. */
export function useAnalyticsIdentity({ isLoading, userId, userType, isSuperAdmin }: Identity): void {
  const identifiedId = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!userId) {
      if (identifiedId.current) {
        identifiedId.current = null;
        resetAnalyticsUser();
      }
      return;
    }
    if (!userType) return;
    if (identifiedId.current && identifiedId.current !== userId) resetAnalyticsUser();
    identifiedId.current = userId;
    identifyUser(userId, { user_type: userType, is_super_admin: isSuperAdmin });
  }, [isLoading, userId, userType, isSuperAdmin]);
}
